import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  ContentGroup,
  ExportRule,
  DEFAULT_EXPORT_RULE_SEPARATOR,
} from "../../../pages/playground/types";
import { normalizeFieldName } from "../../../utils/fieldNames";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type ExportRulesSettingsProps = {
  rules: ExportRule[];
  groups: ContentGroup[];
  onChange: (rules: ExportRule[]) => void;
  validationErrors?: Record<string, string>;
};

// Built-in separator presets. Values are the literal string injected between
// merged columns; labels show what the user sees in the chip.
type SeparatorPreset = { value: string; label: string };
const SEPARATOR_PRESETS: SeparatorPreset[] = [
  { value: "\n\n", label: "Blank line (\\n\\n)" },
  { value: "\n", label: "Newline (\\n)" },
  { value: " ", label: "Space" },
  { value: " - ", label: "Dash ( - )" },
  { value: " | ", label: "Pipe ( | )" },
];

// Render a separator value in a way the user can read (escapes whitespace).
function renderSeparator(sep: string): string {
  if (sep === "") return "(empty)";
  return JSON.stringify(sep).slice(1, -1);
}

export default function ExportRulesSettings({
  rules,
  groups,
  onChange,
  validationErrors = {},
}: ExportRulesSettingsProps) {
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  const hasRuleErrors = Object.keys(validationErrors).some((k) =>
    k.startsWith("settings.export_rules"),
  );

  const enabledGroups = groups
    .filter((g) => g.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Groups already used in other rules (a group can only be in one rule)
  const usedGroupIds = new Set(rules.flatMap((r) => r.sourceGroups));

  const handleAddRule = () => {
    const newRule: ExportRule = {
      id: `export_rule_${Date.now()}`,
      label: "",
      sourceGroups: [],
    };
    onChange([...rules, newRule]);
    setExpandedRuleId(newRule.id);
  };

  const handleDeleteRule = (ruleId: string) => {
    onChange(rules.filter((r) => r.id !== ruleId));
    if (expandedRuleId === ruleId) setExpandedRuleId(null);
  };

  const handleRuleChange = (ruleId: string, updated: Partial<ExportRule>) => {
    onChange(rules.map((r) => (r.id === ruleId ? { ...r, ...updated } : r)));
  };

  const toggleGroup = (ruleId: string, groupId: string) => {
    const rule = rules.find((r) => r.id === ruleId);
    if (!rule) return;
    const has = rule.sourceGroups.includes(groupId);
    const newGroups = has
      ? rule.sourceGroups.filter((id) => id !== groupId)
      : [...rule.sourceGroups, groupId];
    handleRuleChange(ruleId, { sourceGroups: newGroups });
  };

  return (
    <CollapsibleFormElement title="Export merging columns rules" hasError={hasRuleErrors}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This section allows to define the export rules to merge multiple
        columns into one. The default merging criteria is the newline
        separator. The order of merged content follows the order set in the
        Content Groups list above.
      </Typography>

      {rules.map((rule, ruleIndex) => {
        const isExpanded = expandedRuleId === rule.id;
        const errorPrefix = `settings.export_rules.${ruleIndex}`;
        const ruleErrors = Object.entries(validationErrors)
          .filter(([k]) => k.startsWith(errorPrefix))
          .map(([, m]) => m);
        const hasError = ruleErrors.length > 0;
        const selectedLabels = rule.sourceGroups
          .map((id) => groups.find((g) => g.id === id)?.label)
          .filter(Boolean);

        const ruleSeparator =
          rule.separator !== undefined
            ? rule.separator
            : DEFAULT_EXPORT_RULE_SEPARATOR;
        const isCustomSeparator = !SEPARATOR_PRESETS.some(
          (p) => p.value === ruleSeparator
        );

        return (
          <Paper
            key={rule.id}
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 1,
              cursor: "pointer",
              boxShadow: hasError ? "0px 0px 0px 1px red" : "none",
            }}
            onClick={() =>
              setExpandedRuleId(isExpanded ? null : rule.id)
            }
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <TextField
                  size="small"
                  value={rule.label}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    handleRuleChange(rule.id, { label: e.target.value })
                  }
                  placeholder="Column name (e.g. In Evidenza)"
                  sx={{
                    maxWidth: 280,
                    "& .MuiInputBase-root": { height: 32, fontWeight: 500 },
                  }}
                />
                {rule.label && (
                  <Chip
                    size="small"
                    label={normalizeFieldName(rule.label)}
                    variant="outlined"
                    color="default"
                  />
                )}
              </Box>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRule(rule.id);
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Summary when collapsed */}
            {!isExpanded && selectedLabels.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
                {selectedLabels.map((label) => (
                  <Chip key={label} label={label} size="small" variant="outlined" />
                ))}
                <Chip
                  label={`Separator: ${renderSeparator(ruleSeparator)}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              </Box>
            )}

            {/* Errors visible even when collapsed */}
            {!isExpanded && ruleErrors.length > 0 && (
              <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid", borderColor: "error.main" }}>
                {ruleErrors.map((err, i) => (
                  <Typography key={i} variant="body2" color="error" sx={{ display: "block" }}>
                    {"•"} {err}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Expanded: group selection */}
            {isExpanded && (
              <Box
                sx={{ mt: 1.5, pl: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography variant="caption" color="text.secondary">
                  Select content groups to merge (kept in the order shown
                  above):
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5 }}>
                  {enabledGroups.map((group) => {
                    const isSelected = rule.sourceGroups.includes(group.id);
                    const usedByOther =
                      !isSelected && usedGroupIds.has(group.id);

                    return (
                      <FormControlLabel
                        key={group.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={usedByOther}
                            onChange={() => toggleGroup(rule.id, group.id)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {group.label}
                            {usedByOther && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (used in another rule)
                              </Typography>
                            )}
                          </Typography>
                        }
                        sx={{ ml: 0, height: 32 }}
                      />
                    );
                  })}
                </Box>

                {/* Separator picker */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Separator between merged columns:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {SEPARATOR_PRESETS.map((preset) => {
                      const isSelected =
                        !isCustomSeparator && ruleSeparator === preset.value;
                      return (
                        <Chip
                          key={preset.label}
                          label={preset.label}
                          size="small"
                          variant={isSelected ? "filled" : "outlined"}
                          color={isSelected ? "primary" : "default"}
                          onClick={() =>
                            handleRuleChange(rule.id, {
                              separator: preset.value,
                            })
                          }
                          sx={{ cursor: "pointer" }}
                        />
                      );
                    })}
                    <Chip
                      label="Custom"
                      size="small"
                      variant={isCustomSeparator ? "filled" : "outlined"}
                      color={isCustomSeparator ? "primary" : "default"}
                      onClick={() =>
                        handleRuleChange(rule.id, {
                          separator: isCustomSeparator ? ruleSeparator : "",
                        })
                      }
                      sx={{ cursor: "pointer" }}
                    />
                    {isCustomSeparator && (
                      <TextField
                        size="small"
                        value={ruleSeparator}
                        placeholder="e.g. , "
                        onChange={(e) =>
                          handleRuleChange(rule.id, {
                            separator: e.target.value,
                          })
                        }
                        sx={{
                          width: 160,
                          "& .MuiInputBase-root": { height: 30 },
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Active separator:{" "}
                    <code>{renderSeparator(ruleSeparator)}</code>
                  </Typography>
                </Box>

                {/* Errors */}
                {ruleErrors.length > 0 && (
                  <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid", borderColor: "error.main" }}>
                    {ruleErrors.map((err, i) => (
                      <Typography key={i} variant="body2" color="error" sx={{ display: "block" }}>
                        {"•"} {err}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Preview */}
                {rule.sourceGroups.length > 0 && rule.label && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Export column: {normalizeFieldName(rule.label)}
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        whiteSpace: "pre-wrap",
                        m: 0,
                      }}
                    >
                      {rule.sourceGroups
                        .map(
                          (id) =>
                            `[${groups.find((g) => g.id === id)?.label || id} content]`
                        )
                        .join(ruleSeparator)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        );
      })}

      <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
        >
          Add Export Rule
        </Button>
      </Box>
    </CollapsibleFormElement>
  );
}
